import Div from '@ui/layout/Div'
import { func, object } from 'prop-types'
import Modal from '@ui/component/Modal'
import Buttons from '@ui/elements/Buttons'
import Icon from '@ui/elements/Icon'
import TextType from '@ui/inputs/TextType'
import { useDispatch, useSelector } from 'react-redux'
import { updateSterilizationCompanyCallBuildings } from '@store/slice/sterilizationSlice'
import { updateNotification, updateValidationPage } from '@store/slice/mainSlice'
import { useTranslation } from 'react-i18next'
import { post } from '@util/xhrAPI'
import useToggle from '@hook/useToggle'
import useFormData from "@hook/useFormData"
import { ROLE_PUBLIC } from "@router/Routes"
import { useNavigate } from 'react-router'
import videoBuildingsMap from '@assets/media/buildingsMap.mp4'
import { useEffect, useState } from 'react'

const CompanySterilizationCallBuildingsMapManager = ({ activeBuilding, setActiveBuilding }) => {
  const { sterilizationCompanyCallBuildings, sterilizationCompanyCallFormObject } = useSelector(state => state.sterilization)
  const { data, mission } = useSelector(state => state.main)
  const { missionBillings } = data
  const { callType } = sterilizationCompanyCallFormObject
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [t] = useTranslation('sterilization')
  const { path, area, name } = activeBuilding
  const [isListing, toggleListing] = useToggle(false)
  const [isLoading, toggleLoading] = useToggle(false)
  const [isModalActive, toggleModal] = useToggle(false)
  const renderFormData = useFormData()
  const [totalArea, setTotalArea] = useState(0)
  const [surfaceCost, setSurfaceCost] = useState(0)

  const getUnitPrice = () => {
    const billingCheckProp = callType.charAt(0).toUpperCase() + callType.slice(1)
    if (
      'surface' === mission.billingType.code
      && (
        false !== ['company', 'coOwnership'].indexOf(callType) && mission[`is${billingCheckProp}Billed`]
      )) {
      setSurfaceCost(0)

      const billingRange = Object.values(missionBillings).filter(billing => {
        if (billing.min <= totalArea && billing.max >= totalArea) {
          return billing
        }
      })[0]
      if (billingRange) {
        const cost = billingRange[`${callType}Cost`] ? billingRange[`${callType}Cost`] : billingRange['cost']
        setSurfaceCost(cost)
      }
    }
  }

  const addToBuildingsList = async () => {
    const buildingsList = sterilizationCompanyCallBuildings.filter(building => building.key !== activeBuilding.key)
    await dispatch(updateSterilizationCompanyCallBuildings([
      ...buildingsList,
      { ...activeBuilding, key: Math.random().toString(36).substring(2, 7) }
    ]))
    if (!isListing) {
      toggleListing()
    }
    setTotalArea(totalArea + parseFloat(activeBuilding.area))
    resetPath()
  }

  const toggleBuilding = building => setActiveBuilding({ ...building })
  const removeBuilding = buildingKey => {
    const buildingsList = sterilizationCompanyCallBuildings.filter(building => {
      if (building.key === buildingKey) {
        setTotalArea(totalArea - parseFloat(building.area))
      }
      return building.key !== buildingKey
    })
    dispatch(updateSterilizationCompanyCallBuildings(buildingsList))
    if (!isListing || !sterilizationCompanyCallBuildings.length) {
      toggleListing()
    }
  }

  const calculateArea = async () => {
    if (path.length < 3) return

    const area = await window.google.maps.geometry.spherical.computeArea(
      path.map(p => new window.google.maps.LatLng(p.lat, p.lng))
    )
    setActiveBuilding({ ...activeBuilding, area: area.toFixed(2) })
  }

  const resetPath = () => setActiveBuilding({ path: [], area: 0, name: '' })

  const updateBuildingName = e => setActiveBuilding({ ...activeBuilding, name: e.target.value })

  const validateBuildingsList = () => {
    const sterilizationCallId = window.sessionStorage.getItem('lastCompanyCall')
    toggleLoading()
    post(`${import.meta.env.VITE_API_URL}/public/sterilization-calls/buildings`,
      renderFormData({ buildings: { ...sterilizationCompanyCallBuildings }, id: sterilizationCallId }, ['buildings']),
      response => {
        const { code, validationPage } = response
        if (200 === code) {
          dispatch(updateNotification({ type: 'is-success', message: t('messages.call_created_successfuly') }))
          dispatch(updateValidationPage(validationPage))
          navigate(ROLE_PUBLIC['publicValidation'])
          toggleLoading()
        }
      })
  }

  const listBuildings = () => {
    const buildings = sterilizationCompanyCallBuildings.map(building => {
      const { name, area, key } = building
      return (
        <Div cssClass='building' key={key}>
          <h5><span>{name}</span>: {area}m² </h5>
          <Div cssClass='options'>
            <Icon
              icon={`icon-eye${activeBuilding.name === building.name ? '-blocked' : ''}`}
              callback={toggleBuilding}
              value={building}
              spanClass='view'
            />
            <Icon
              icon={`icon-bin`}
              callback={removeBuilding}
              value={key}
              spanClass='remove'
            />
          </Div>
        </Div>
      )
    })
    return buildings
  }

  useEffect(() => {
    getUnitPrice()
  }, [totalArea, missionBillings])

  return (
    <>
      <Div cssClass='buildings-manager'>
        <Div cssClass='manager-header'>
          <p className='description'>
            {t('labels.clic_buildings_angles')}
          </p>
          <Icon callback={toggleModal} icon='icon-lightbulb_outline' spanClass='help-icon' />
        </Div>

        {path.length >= 3 && <p><strong>{t('labels.surface')}:</strong> {area > 0 ? `${area} m²` : t('labels.non_calculates')}</p>}
        <TextType
          value={name || ''}
          onChange={updateBuildingName}
          placeholder={t('form.building_name')}
          cssClass={0 === area ? 'is-hidden' : ''}
        />
        <Div cssClass='buildings-actions'>
          <Buttons buttons={[{
            text: t('actions.calculate_area'),
            disabled: path.length < 3,
            callback: calculateArea,
            cssClass: `is-small is-rounded ${path.length < 3 ? 'is-static' : 'is-info'} ${area > 0 ? 'is-hidden' : ''}`
          }, {
            text: t('actions.add_to_buildings'),
            disabled: 0 === area,
            callback: addToBuildingsList,
            cssClass: `is-small is-rounded ${0 === area ? 'is-static' : 'is-success'} ${0 === area ? 'is-hidden' : ''}`
          }, {
            text: t('actions.reset_building'),
            disabled: 0 === path.length,
            callback: resetPath,
            cssClass: `is-small is-rounded ${path.length < 3 ? 'is-static' : 'is-warning'}`
          }]} />
        </Div>

        <Div cssClass={`buildings-header ${sterilizationCompanyCallBuildings.length ? 'is-filled' : ''}`} otherProps={{ onClick: toggleListing }}>
          <h3>{t('labels.buildings')} <span>({sterilizationCompanyCallBuildings.length})</span></h3>
          <Icon icon={`icon-keyboard_arrow_${isListing ? 'down' : 'up'}`} />
        </Div>
        <Div cssClass={`buildings-list ${isListing ? '' : 'is-hidden'}`} >
          {listBuildings()}
          <Div cssClass='costs'>
            <p><label>{t('labels.total_area')} : </label> <span>{totalArea.toFixed(2)} m²</span></p>
            <p><label>{t('labels.price_m2')} : </label> <span>{surfaceCost ? `${surfaceCost} € / m²` : t('labels.free')}</span></p>
            <p className='total-cost'>
              <label>{t('labels.total_cost')} : </label>
              <span>{surfaceCost ? `${(totalArea * parseFloat(surfaceCost)).toFixed(2)} €` : t('labels.free')}</span>
            </p>
          </Div>
          <Div cssClass='buildings-list-actions'>
            <Buttons buttons={{
              text: t('actions.validate_buildings_list'),
              disabled: 0 === sterilizationCompanyCallBuildings.length,
              callback: validateBuildingsList,
              cssClass: `is-small is-success validate ${isLoading ? 'is-loading' : ''}`
            }}
            />
          </Div>
        </Div>

      </Div>
      <Modal cssClass='create-element-modal' isActive={isModalActive} closeCallback={toggleModal} closeAction={true}>
        <video src={videoBuildingsMap} controls></video>
      </Modal>
    </>
  )
}

CompanySterilizationCallBuildingsMapManager.propTypes = {
  activeBuilding: object,
  setActiveBuilding: func
}

export default CompanySterilizationCallBuildingsMapManager
